import React, { useState, useEffect } from 'react';
import { contentAPI, teamsAPI, matchesAPI } from '../../services/api';
import { FaMinusCircle, FaTrash, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const PointsDeductionSection = ({ teams: initialTeams, matches: initialMatches }) => {
  const { language } = useLanguage();
  const [penalties, setPenalties] = useState([]);
  const [teams, setTeams] = useState(initialTeams || []);
  const [matches, setMatches] = useState(initialMatches || []);
  const [motifs, setMotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'motifs'
  const [formData, setFormData] = useState({
    team_id: '',
    match_id: '',
    points: 1,
    reason: '',
    reason_ar: ''
  });
  const [motifForm, setMotifForm] = useState({
    name: '',
    name_ar: '',
    default_points: 1
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (initialTeams) setTeams(initialTeams);
    if (initialMatches) setMatches(initialMatches);
  }, [initialTeams, initialMatches]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch penalties
      try {
        const res = await contentAPI.getPenalties();
        setPenalties(res.data || []);
      } catch (e) { console.error('Error penalties:', e); }

      // Fetch teams if not provided
      if (!initialTeams || initialTeams.length === 0) {
        try {
          const res = await teamsAPI.getAll();
          setTeams(res.data || []);
        } catch (e) { console.error('Error teams:', e); }
      }

      // Fetch matches if not provided
      if (!initialMatches || initialMatches.length === 0) {
        try {
          const res = await matchesAPI.getAll();
          setMatches(res.data || []);
        } catch (e) { console.error('Error matches:', e); }
      }

      // Fetch motifs
      try {
        const res = await contentAPI.getPenaltyMotifs();
        setMotifs(res.data || []);
      } catch (e) { console.error('Error motifs:', e); }

    } catch (error) {
      console.error('Global load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.team_id || !formData.points) {
        alert(language === 'ar' ? 'يرجى ملء الحقول الإجبارية' : 'Veuillez remplir les champs obligatoires');
        return;
      }
      await contentAPI.createPenalty(formData);
      alert(language === 'ar' ? 'تمت إضافة العقوبة بنجاح' : 'Pénalité ajoutée avec succès');
      setFormData({ team_id: '', match_id: '', points: 1, reason: '', reason_ar: '' });
      setActiveTab('list');
      loadData();
    } catch (error) {
      alert(language === 'ar' ? 'خطأ أثناء الإضافة' : 'Erreur lors de l\'ajout');
    }
  };

  const handleMotifSubmit = async (e) => {
    e.preventDefault();
    try {
      await contentAPI.createPenaltyMotif(motifForm);
      alert(language === 'ar' ? 'تمت إضافة السبب بنجاح' : 'Motif ajouté avec succès');
      setMotifForm({ name: '', name_ar: '', default_points: 1 });
      loadData();
    } catch (error) {
      alert('Erreur lors de l\'ajout du motif');
    }
  };

  const deleteMotif = async (id) => {
    if (window.confirm('Supprimer ce motif ?')) {
      try {
        await contentAPI.deletePenaltyMotif(id);
        loadData();
      } catch (error) { alert('Erreur'); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette pénalité ?')) {
      try {
        await contentAPI.deletePenalty(id);
        loadData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };


  if (loading) return <div style={styles.loading}>Chargement...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaMinusCircle style={{ marginRight: '10px' }} />
          {language === 'ar' ? 'سحب النقاط' : 'Retrait de Points'}
        </h1>
        <div style={styles.tabGroup}>
          <button 
            style={{...styles.tabBtn, ...(activeTab === 'list' ? styles.tabActive : {})}} 
            onClick={() => setActiveTab('list')}
          >
            {language === 'ar' ? 'السجل' : 'Historique'}
          </button>
          <button 
            style={{...styles.tabBtn, ...(activeTab === 'create' ? styles.tabActive : {})}} 
            onClick={() => setActiveTab('create')}
          >
            {language === 'ar' ? '+ عقوبة' : '+ Sanction'}
          </button>
          <button 
            style={{...styles.tabBtn, ...(activeTab === 'motifs' ? styles.tabActive : {})}} 
            onClick={() => setActiveTab('motifs')}
          >
            {language === 'ar' ? 'الأسباب' : 'Motifs/Types'}
          </button>
        </div>
        <button 
          onClick={loadData} 
          style={{
            padding: '8px 15px', 
            borderRadius: '8px', 
            background: '#ffffff', 
            border: '1px solid #c41e3a',
            color: '#c41e3a',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <span>🔄</span>
          {language === 'ar' ? 'تحديث البيانات' : 'Actualiser'}
        </button>
      </div>

      {activeTab === 'create' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>{language === 'ar' ? 'إضافة عقوبة جديدة' : 'Nouvelle Sanction'}</h2>
          
          <div style={styles.presets}>
            {motifs.map((m, idx) => (
              <button 
                key={idx} 
                style={styles.presetBtn}
                onClick={() => setFormData({
                  ...formData,
                  points: m.default_points,
                  reason: m.name,
                  reason_ar: m.name_ar
                })}
              >
                <strong>{language === 'ar' ? m.name_ar : m.name}</strong>
                <span>-{m.default_points} Pts</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{language === 'ar' ? 'الفريق المعاقب' : 'Équipe sanctionnée'}</label>
                <select 
                  style={styles.select}
                  value={formData.team_id}
                  onChange={(e) => setFormData({...formData, team_id: e.target.value})}
                  required
                >
                  <option value="">{language === 'ar' ? '--- اختر الفريق ---' : '--- Choisir une équipe ---'}</option>
                  {teams.length > 0 ? (
                    teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.city || '-'} - Gr {t.group_number || 1})</option>
                    ))
                  ) : (
                    <option disabled>{language === 'ar' ? 'جاري التحميل أو لا يوجد فرق...' : 'Chargement ou aucune équipe...'}</option>
                  )}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{language === 'ar' ? 'المباراة المرتبطة (اختياري)' : 'Match associé (Optionnel)'}</label>
                <select 
                  style={styles.select}
                  value={formData.match_id}
                  onChange={(e) => setFormData({...formData, match_id: e.target.value})}
                >
                  <option value="">{language === 'ar' ? '--- لا يوجد ---' : '--- Aucun ---'}</option>
                  {(matches || [])
                    .filter(m => !formData.team_id || m.home_team_id == formData.team_id || m.away_team_id == formData.team_id)
                    .map(m => (
                    <option key={m.id} value={m.id}>
                      [{language === 'ar' ? 'جولة' : 'J'} {m.round || '-'}] {m.home_team_name} vs {m.away_team_name} ({new Date(m.match_date).toLocaleDateString()})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{language === 'ar' ? 'النقاط المسحوبة (1-6)' : 'Points retirés (1-6)'}</label>
              <input 
                type="number" 
                min="1" 
                max="6" 
                style={styles.input}
                value={formData.points}
                onChange={(e) => setFormData({...formData, points: parseInt(e.target.value)})}
                required
              />
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Raison / Motif (FR)</label>
                <input 
                  type="text" 
                  style={styles.input}
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="Ex: Amende non payée"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>السبب (AR)</label>
                <input 
                  type="text" 
                  style={{...styles.input, textAlign: 'right'}}
                  value={formData.reason_ar}
                  onChange={(e) => setFormData({...formData, reason_ar: e.target.value})}
                  placeholder="مثال: عدم خلاص خطية مالية"
                  required
                />
              </div>
            </div>

            <button type="submit" style={styles.submitBtn}>
              {language === 'ar' ? 'تأكيد وحفظ العقوبة' : 'Enregistrer la Sanction'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'motifs' && (
        <div style={styles.flexGrid}>
          <div style={{...styles.card, flex: 1}}>
            <h2 style={styles.cardTitle}>{language === 'ar' ? 'إضافة سبب جديد' : 'Nouveau Type de Motif'}</h2>
            <form onSubmit={handleMotifSubmit} style={styles.form}>
              <input 
                type="text" 
                placeholder="Nom du motif (FR)" 
                style={styles.input}
                value={motifForm.name}
                onChange={(e) => setMotifForm({...motifForm, name: e.target.value})}
                required
              />
              <input 
                type="text" 
                placeholder="السبب (AR)" 
                style={{...styles.input, textAlign: 'right'}}
                value={motifForm.name_ar}
                onChange={(e) => setMotifForm({...motifForm, name_ar: e.target.value})}
                required
              />
              <input 
                type="number" 
                placeholder="Points par défaut" 
                style={styles.input}
                value={motifForm.default_points}
                onChange={(e) => setMotifForm({...motifForm, default_points: parseInt(e.target.value)})}
                required
              />
              <button type="submit" style={styles.submitBtn}>
                {language === 'ar' ? 'إضافة للقائمة' : 'Ajouter aux motifs'}
              </button>
            </form>
          </div>

          <div style={{...styles.card, flex: 1.5}}>
            <h2 style={styles.cardTitle}>{language === 'ar' ? 'قائمة الأسباب المسجلة' : 'Motifs enregistrés'}</h2>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={styles.th}>{language === 'ar' ? 'السبب' : 'Motif'}</th>
                  <th style={styles.th}>Pts</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {motifs.map(m => (
                  <tr key={m.id} style={styles.tr}>
                    <td style={styles.td}>{language === 'ar' ? m.name_ar : m.name}</td>
                    <td style={styles.td}>{m.default_points}</td>
                    <td style={styles.td}>
                      <button onClick={() => deleteMotif(m.id)} style={styles.deleteBtn}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>{language === 'ar' ? 'سجل العقوبات' : 'Historique des Sanctions'}</h2>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={styles.th}>{language === 'ar' ? 'الفريق' : 'Équipe'}</th>
                  <th style={styles.th}>Points</th>
                  <th style={styles.th}>{language === 'ar' ? 'السبب' : 'Raison'}</th>
                  <th style={styles.th}>{language === 'ar' ? 'المباراة' : 'Match'}</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {penalties.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={styles.noData}>Aucune pénalité enregistrée.</td>
                  </tr>
                ) : (
                  penalties.map(p => (
                    <tr key={p.id} style={styles.tr}>
                      <td style={styles.td}><strong>{p.team_name}</strong></td>
                      <td style={styles.td}>
                        <span style={styles.pointsBadge}>-{p.points} Pts</span>
                      </td>
                      <td style={styles.td}>{language === 'ar' ? p.reason_ar : p.reason}</td>
                      <td style={styles.td}>
                        {p.match_id ? (
                          <div style={{fontSize: '12px'}}>
                            {p.home_name} vs {p.away_name}<br/>
                            <span style={{color: '#999'}}>{new Date(p.match_date).toLocaleDateString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        <button 
                          style={styles.deleteBtn}
                          onClick={() => handleDelete(p.id)}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

};

const styles = {
  container: { padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '28px', fontWeight: 'bold', display: 'flex', alignItems: 'center' },
  tabGroup: { display: 'flex', gap: '10px', background: '#eee', padding: '5px', borderRadius: '10px' },
  tabBtn: { padding: '8px 20px', border: 'none', background: 'transparent', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
  tabActive: { background: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', color: '#1a365d' },
  card: { background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', marginBottom: '30px' },
  cardTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '10px' },
  presets: { display: 'flex', gap: '15px', marginBottom: '25px', flexWrap: 'wrap' },
  presetBtn: { 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    padding: '12px', 
    background: '#f8f9fa', 
    border: '1px solid #dee2e6', 
    borderRadius: '10px', 
    cursor: 'pointer',
    minWidth: '150px',
    transition: 'all 0.2s'
  },
  form: { display: 'flex', flexDirection: 'column', gap: '15px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
  flexGrid: { display: 'flex', gap: '25px', flexWrap: 'wrap' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 },
  label: { fontSize: '14px', fontWeight: 'bold', color: '#666' },
  input: { padding: '10px', border: '1px solid #ddd', borderRadius: '8px' },
  select: { padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' },
  submitBtn: { padding: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thRow: { background: '#f8f9fa' },
  th: { padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#666', fontSize: '14px' },
  tr: { borderBottom: '1px solid #eee', transition: 'background 0.2s' },
  td: { padding: '15px', fontSize: '14px' },
  pointsBadge: { padding: '4px 10px', background: '#f8d7da', color: '#c41e3a', borderRadius: '20px', fontWeight: 'bold', fontSize: '14px' },
  deleteBtn: { background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '18px' },
  noData: { textAlign: 'center', padding: '30px', color: '#999' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '18px' }
};

export default PointsDeductionSection;
